import type {
  Dimension,
  Question,
  QuestionId,
  ScoringWeights,
  BudgetBucket,
} from './types';

// Bumped 1→2 on 2026-05-25 (Phase 1 of quiz coverage expansion):
//   - Adds visa_pathway, tax_residency_intent, ties, income_amount questions.
//   - Drops `priorities.culture` option (was a no-op).
//   - Splits `priorities.sports` into mountain_sports + water_sports.
//   - Adds horizon.next_year option (12–24 mo gap).
// Persisted scenarios on v1 fail the loader's schemaVersion check and the
// user is shown a clean blank quiz — verified safer than auto-migrating
// because the new questions materially change scoring.
//
// NOT bumped on 2026-05-28 (CV.2 M1 follow-ups):
//   - Adds career_local_language / career_timeline / career_sponsorship /
//     career_seniority. All gated by visible_when, default-absent, and do
//     NOT change scoring in this PR (scoreJobsCareer arrives behind
//     SCORING_V3_CAREER flag in a follow-up PR per CV.4 §4-6).
//   - Old sessions render unchanged: the 4 new questions are simply absent
//     from `answers`, the renderer treats them as unanswered, and the gate
//     value (already in v2) drives visible_when. Bumping would wipe v2
//     sessions without functional benefit.
export const QUIZ_SCHEMA_VERSION = 2;

// Feature flag reserved for the generic, profession-agnostic jobs scoring
// overlay (`scoreJobsCareer()` — CV.0.1 §7 / CV.4): a light adjustment that,
// when `career_intent = primary`, up-weights `jobs` and rewards high
// english-at-work using two country atoms (market_strength,
// english_workplace_band). NOT yet implemented. The original four IT-framed
// follow-up questions that this flag used to gate were removed from the quiz
// in CV.0.1 — work depth now lives in the next-steps "Work & income" phase,
// not in pre-destination quiz questions. Flip on only after that scoring
// lands, behind an A/B cohort.
// See docs/roadmap/career-vector/CV.0.1-conscious-relocation-repositioning.md.
export const SCORING_V3_CAREER: boolean = false;

export interface BudgetBucketDef {
  id: BudgetBucket;
  label_key: string;
  min_eur: number;
  // Inclusive upper bound for matching, or +Infinity for the top bucket.
  max_eur: number;
}

export const BUDGET_BUCKETS: readonly BudgetBucketDef[] = [
  { id: 1, label_key: 'quiz.budget.bucket.lt500', min_eur: 0, max_eur: 500 },
  { id: 2, label_key: 'quiz.budget.bucket.500to1000', min_eur: 500, max_eur: 1000 },
  { id: 3, label_key: 'quiz.budget.bucket.1000to2000', min_eur: 1000, max_eur: 2000 },
  { id: 4, label_key: 'quiz.budget.bucket.gt2000', min_eur: 2000, max_eur: Number.POSITIVE_INFINITY },
] as const;

export const DEFAULT_WEIGHTS: ScoringWeights = {
  visa: 1.0,
  tax: 1.0,
  cost: 1.0,
  climate: 1.0,
  healthcare: 1.0,
  safety: 1.0,
  language: 1.0,
  jobs: 1.0,
  family: 0.5,
  banking: 1.0,
};

export const QUESTIONS: Question[] = [
  {
    id: 'horizon',
    type: 'single',
    label_key: 'quiz.q.horizon.label',
    options: [
      { id: 'now', label_key: 'quiz.q.horizon.opt.now', weights: { visa: 0.5 } },
      { id: 'soon', label_key: 'quiz.q.horizon.opt.soon' },
      // Phase 1 add: 12–24 month window. Was missing — gap between
      // "within a year" and "exploring options" caught real planners.
      { id: 'next_year', label_key: 'quiz.q.horizon.opt.next_year' },
      { id: 'explore', label_key: 'quiz.q.horizon.opt.explore' },
      { id: 'already', label_key: 'quiz.q.horizon.opt.already' },
    ],
  },
  {
    id: 'passport',
    type: 'single',
    label_key: 'quiz.q.passport.label',
    description_key: 'quiz.q.passport.description',
    options: [
      { id: 'eu', label_key: 'quiz.q.passport.opt.eu' },
      { id: 'anglo', label_key: 'quiz.q.passport.opt.anglo' },
      // Stage 4.7 (2026-05-24): RU/BY/UA are tracked separately because
      // host countries treat them very differently — bundling them as
      // "cis" caused Estonia to surface in a Russian user's top results
      // even though banking and notary access is effectively closed.
      { id: 'ru', label_key: 'quiz.q.passport.opt.ru' },
      { id: 'by', label_key: 'quiz.q.passport.opt.by' },
      { id: 'ua', label_key: 'quiz.q.passport.opt.ua' },
      { id: 'other', label_key: 'quiz.q.passport.opt.other' },
    ],
  },
  // Phase 1 (2026-05-25): visa-pathway question. Cascades into a lot
  // (work permits, golden visa, ancestry — these radically prune the list)
  // so it sits right after `passport`. Multi-select because real users
  // explore 2–3 paths in parallel. `unknown_exploring` is a soft signal
  // that keeps scoring neutral (no penalty for indecision).
  // Option weights bump `visa` dimension when the user expresses ANY
  // pathway intent — they're saying "visa specifics matter to me" so the
  // dimension's weight in the geometric mean goes up. The pathway-specific
  // multiplier (boost for matching country, penalty for non-matching) is
  // applied in scoring.ts once country_visa_extra.json data lands.
  {
    id: 'visa_pathway',
    type: 'multi',
    label_key: 'quiz.q.visa_pathway.label',
    description_key: 'quiz.q.visa_pathway.description',
    skippable: true,
    options: [
      { id: 'digital_nomad', label_key: 'quiz.q.visa_pathway.opt.digital_nomad', weights: { visa: 0.5 } },
      { id: 'freelance_self_employed', label_key: 'quiz.q.visa_pathway.opt.freelance_self_employed', weights: { visa: 0.5 } },
      { id: 'golden_investor', label_key: 'quiz.q.visa_pathway.opt.golden_investor', weights: { visa: 0.5 } },
      { id: 'family_reunion', label_key: 'quiz.q.visa_pathway.opt.family_reunion', weights: { visa: 0.5 } },
      { id: 'ancestry', label_key: 'quiz.q.visa_pathway.opt.ancestry', weights: { visa: 0.5 } },
      { id: 'work_sponsor', label_key: 'quiz.q.visa_pathway.opt.work_sponsor', weights: { visa: 0.5 } },
      { id: 'student_to_perm', label_key: 'quiz.q.visa_pathway.opt.student_to_perm', weights: { visa: 0.5 } },
      { id: 'refugee_protection', label_key: 'quiz.q.visa_pathway.opt.refugee_protection' },
      { id: 'unknown_exploring', label_key: 'quiz.q.visa_pathway.opt.unknown_exploring' },
    ],
  },
  {
    id: 'climate',
    type: 'multi',
    label_key: 'quiz.q.climate.label',
    multi_max: 2,
    options: [
      { id: 'alps', label_key: 'quiz.q.climate.opt.alps', weights: { climate: 1.0 } },
      { id: 'mediterranean', label_key: 'quiz.q.climate.opt.mediterranean', weights: { climate: 1.0 } },
      { id: 'continental', label_key: 'quiz.q.climate.opt.continental', weights: { climate: 1.0 } },
      { id: 'tropical', label_key: 'quiz.q.climate.opt.tropical', weights: { climate: 1.0 } },
      { id: 'northern', label_key: 'quiz.q.climate.opt.northern', weights: { climate: 1.0 } },
      { id: 'any', label_key: 'quiz.q.climate.opt.any' },
    ],
  },
  {
    id: 'urban_density',
    type: 'multi',
    multi_max: 2,
    label_key: 'quiz.q.urban_density.label',
    description_key: 'quiz.q.urban_density.description',
    options: [
      {
        id: 'capital',
        label_key: 'quiz.q.urban_density.opt.capital',
        help_key: 'quiz.q.urban_density.help.capital',
        weights: { jobs: 0.5 },
      },
      {
        id: 'mid_city',
        label_key: 'quiz.q.urban_density.opt.mid_city',
        help_key: 'quiz.q.urban_density.help.mid_city',
      },
      {
        id: 'town',
        label_key: 'quiz.q.urban_density.opt.town',
        help_key: 'quiz.q.urban_density.help.town',
        weights: { language: 0.5 },
      },
      {
        id: 'coastal',
        label_key: 'quiz.q.urban_density.opt.coastal',
        help_key: 'quiz.q.urban_density.help.coastal',
        weights: { climate: 0.5 },
      },
    ],
  },
  {
    id: 'budget',
    type: 'budget-slider',
    label_key: 'quiz.q.budget.label',
    description_key: 'quiz.q.budget.description',
    skippable: true,
  },
  {
    id: 'priorities',
    type: 'multi',
    label_key: 'quiz.q.priorities.label',
    multi_max: 3,
    options: [
      { id: 'safety', label_key: 'quiz.q.priorities.opt.safety', weights: { safety: 1.5 } },
      { id: 'healthcare', label_key: 'quiz.q.priorities.opt.healthcare', weights: { healthcare: 1.5 } },
      { id: 'schools', label_key: 'quiz.q.priorities.opt.schools', weights: { family: 1.5, language: 0.5 } },
      { id: 'transport', label_key: 'quiz.q.priorities.opt.transport', weights: { jobs: 0.5 } },
      { id: 'nature', label_key: 'quiz.q.priorities.opt.nature', weights: { climate: 0.5 } },
      // Phase 1: `culture` removed (was a no-op — no weights attached, no
      // dimension boosted, pure visual filler). Persisted scenarios that
      // had it become slightly orphaned but render fine (option no longer
      // in the picker UI; old saved value just isn't an active filter).
      { id: 'ru-community', label_key: 'quiz.q.priorities.opt.ru-community', weights: { language: 0.5, banking: 0.5 } },
      // Phase 1: `sports` split into two — alpine vs water sport users
      // want very different climates. mountain_sports boosts climate
      // alpine-tagged countries; water_sports boosts coastal/mediterranean.
      { id: 'mountain_sports', label_key: 'quiz.q.priorities.opt.mountain_sports', weights: { climate: 0.8 } },
      { id: 'water_sports', label_key: 'quiz.q.priorities.opt.water_sports', weights: { climate: 0.8 } },
    ],
  },
  {
    id: 'income',
    type: 'multi',
    multi_max: 2,
    label_key: 'quiz.q.income.label',
    options: [
      { id: 'remote', label_key: 'quiz.q.income.opt.remote', weights: { jobs: 1.0, tax: 0.5 } },
      { id: 'local', label_key: 'quiz.q.income.opt.local', weights: { jobs: 1.0, language: 1.0 } },
      { id: 'business', label_key: 'quiz.q.income.opt.business', weights: { tax: 1.5, banking: 0.5 } },
      { id: 'passive', label_key: 'quiz.q.income.opt.passive', weights: { tax: 1.0, banking: 1.0 } },
    ],
  },
  // Phase 1: income AMOUNT (separate from type). Most DN visas require a
  // minimum proof of monthly income (€2.5–3k typical) — without this we
  // can't tell a user with €1k/mo why DN-friendly countries are out of
  // reach. Skippable; we treat skip as "no info" not as low bucket.
  {
    id: 'income_amount',
    type: 'single',
    skippable: true,
    label_key: 'quiz.q.income_amount.label',
    description_key: 'quiz.q.income_amount.description',
    options: [
      { id: 'lt1500', label_key: 'quiz.q.income_amount.opt.lt1500' },
      { id: '1500to3000', label_key: 'quiz.q.income_amount.opt.1500to3000' },
      { id: '3000to6000', label_key: 'quiz.q.income_amount.opt.3000to6000' },
      { id: 'gt6000', label_key: 'quiz.q.income_amount.opt.gt6000' },
    ],
  },
  // Phase 1: tax residency intent. Distinguishes the buyer of a Portugal
  // NHR 2.0 / Cyprus non-dom / Georgia 1% IE from a US expat who wants to
  // keep filing 1040 and not break treaty ties. Scoring multiplier kicks
  // in once country.tax.territorial / .dtt_with data lands; for now
  // option.weights bump generic tax dim so the intent is reflected.
  {
    id: 'tax_residency_intent',
    type: 'single',
    skippable: true,
    label_key: 'quiz.q.tax_residency_intent.label',
    description_key: 'quiz.q.tax_residency_intent.description',
    options: [
      { id: 'break_origin', label_key: 'quiz.q.tax_residency_intent.opt.break_origin', weights: { tax: 0.5 } },
      { id: 'keep_origin', label_key: 'quiz.q.tax_residency_intent.opt.keep_origin', weights: { tax: 0.3 } },
      { id: 'dual', label_key: 'quiz.q.tax_residency_intent.opt.dual', weights: { tax: 0.5 } },
      { id: 'treaty_minimize', label_key: 'quiz.q.tax_residency_intent.opt.treaty_minimize', weights: { tax: 0.5 } },
      { id: 'unsure', label_key: 'quiz.q.tax_residency_intent.opt.unsure' },
    ],
  },
  // M0 — career_intent gate. Captured but does NOT change scoring in PoL phase.
  // 3-state opt-in: `primary` (career is the main reason) / `eventual` (will
  // search work in 1–2 yrs) / `none` (independent / DN / retiree). M1+ activates
  // the scoring overlay per CV.4. See docs/roadmap/career-vector/CV.PoL-probe.md
  // and CV.2-quiz-career-gate.md.
  //
  // Schema version intentionally NOT bumped: this question is fully additive,
  // does not change scoring, and bumping would wipe existing user sessions —
  // which would corrupt the PoL completion-rate regression check.
  {
    id: 'career_intent',
    type: 'single',
    skippable: true,
    label_key: 'quiz.q.career_intent.label',
    description_key: 'quiz.q.career_intent.description',
    options: [
      {
        id: 'primary',
        label_key: 'quiz.q.career_intent.opt.primary',
        help_key: 'quiz.q.career_intent.help.primary',
      },
      {
        id: 'eventual',
        label_key: 'quiz.q.career_intent.opt.eventual',
        help_key: 'quiz.q.career_intent.help.eventual',
      },
      {
        id: 'none',
        label_key: 'quiz.q.career_intent.opt.none',
        help_key: 'quiz.q.career_intent.help.none',
      },
    ],
  },
  // CV.0.1 (2026-06-09): the four IT-framed career follow-ups
  // (career_local_language / career_timeline / career_sponsorship /
  // career_seniority) were REMOVED from the quiz. Work is repositioned as an
  // optional, later-stage, profession-agnostic layer — its depth (field +
  // position + work-authorisation + language) now lives in the next-steps
  // "Work & income" phase per chosen country, not in pre-destination quiz
  // questions. Only the field-agnostic `career_intent` gate above remains.
  // See docs/roadmap/career-vector/CV.0.1-conscious-relocation-repositioning.md.
  {
    id: 'family',
    type: 'multi',
    label_key: 'quiz.q.family.label',
    description_key: 'quiz.q.family.description',
    options: [
      { id: 'solo', label_key: 'quiz.q.family.opt.solo' },
      { id: 'partner', label_key: 'quiz.q.family.opt.partner' },
      { id: 'kids-preschool', label_key: 'quiz.q.family.opt.kids-preschool', weights: { family: 1.5, language: 0.5 } },
      { id: 'kids-school', label_key: 'quiz.q.family.opt.kids-school', weights: { family: 1.5, language: 1.0 } },
      { id: 'pets', label_key: 'quiz.q.family.opt.pets' },
    ],
  },
  // Household-size follow-up for cost_of_living.json (2026-08-18). `family`
  // above only tells us kids are present, not how many — but the cost data
  // carries five separate baskets (single/couple/family_1/2/3, see
  // docs/data/COST_OF_LIVING_SCHEMA.md), and family_1 vs family_3 differs by
  // more than most country-to-country gaps. Gated to only appear when a kids
  // option was picked, so childless users never see it. Required (not
  // skippable) once shown — an unanswered count would silently default to
  // family_1 in deriveHouseholdBucket(), understating cost for larger
  // families, so we ask instead of guessing.
  {
    id: 'family_children_count',
    type: 'single',
    label_key: 'quiz.q.family_children_count.label',
    description_key: 'quiz.q.family_children_count.description',
    visible_when: (answers) => {
      const ids = answers.family?.optionIds ?? [];
      return ids.includes('kids-preschool') || ids.includes('kids-school');
    },
    options: [
      { id: '1', label_key: 'quiz.q.family_children_count.opt.1' },
      { id: '2', label_key: 'quiz.q.family_children_count.opt.2' },
      { id: '3plus', label_key: 'quiz.q.family_children_count.opt.3plus' },
    ],
  },
  {
    id: 'languages',
    type: 'multi',
    label_key: 'quiz.q.languages.label',
    options: [
      { id: 'en', label_key: 'quiz.q.languages.opt.en', weights: { language: 0.5 } },
      { id: 'it', label_key: 'quiz.q.languages.opt.it' },
      { id: 'es', label_key: 'quiz.q.languages.opt.es' },
      { id: 'de', label_key: 'quiz.q.languages.opt.de' },
      { id: 'fr', label_key: 'quiz.q.languages.opt.fr' },
      { id: 'pt', label_key: 'quiz.q.languages.opt.pt' },
      { id: 'any', label_key: 'quiz.q.languages.opt.any' },
      { id: 'ru-only', label_key: 'quiz.q.languages.opt.ru-only', weights: { language: 1.5, banking: 1.0 } },
    ],
  },
  {
    id: 'dealbreakers',
    type: 'multi',
    label_key: 'quiz.q.dealbreakers.label',
    description_key: 'quiz.q.dealbreakers.description',
    skippable: true,
    options: [
      { id: 'hot-summer', label_key: 'quiz.q.dealbreakers.opt.hot-summer' },
      { id: 'snow', label_key: 'quiz.q.dealbreakers.opt.snow' },
      { id: 'humidity', label_key: 'quiz.q.dealbreakers.opt.humidity' },
      { id: 'earthquakes', label_key: 'quiz.q.dealbreakers.opt.earthquakes' },
      { id: 'complex-visa', label_key: 'quiz.q.dealbreakers.opt.complex-visa', weights: { visa: 1.5 } },
      { id: 'expensive-healthcare', label_key: 'quiz.q.dealbreakers.opt.expensive-healthcare', weights: { healthcare: 1.5 } },
    ],
  },
  // Existing-ties signal: multi-select across all 30 destinations. Each
  // selected country gets a +10% score boost in ranking (per-country, not
  // flat-to-all). User sees the full list so they can recognise countries
  // they may not have thought of. Skippable = no ties is the baseline.
  {
    id: 'ties',
    type: 'multi',
    skippable: true,
    label_key: 'quiz.q.ties.label',
    description_key: 'quiz.q.ties.description',
    // Options sorted by ISO code for easy scanning.
    // Option ids match CountryCode values so scoring.ts can do a direct lookup.
    options: [
      { id: 'AE', label_key: 'quiz.q.ties.opt.AE' },
      { id: 'AL', label_key: 'quiz.q.ties.opt.AL' },
      { id: 'AR', label_key: 'quiz.q.ties.opt.AR' },
      { id: 'BR', label_key: 'quiz.q.ties.opt.BR' },
      { id: 'CA', label_key: 'quiz.q.ties.opt.CA' },
      { id: 'CL', label_key: 'quiz.q.ties.opt.CL' },
      { id: 'CO', label_key: 'quiz.q.ties.opt.CO' },
      { id: 'CR', label_key: 'quiz.q.ties.opt.CR' },
      { id: 'CY', label_key: 'quiz.q.ties.opt.CY' },
      { id: 'CZ', label_key: 'quiz.q.ties.opt.CZ' },
      { id: 'DE', label_key: 'quiz.q.ties.opt.DE' },
      { id: 'EE', label_key: 'quiz.q.ties.opt.EE' },
      { id: 'ES', label_key: 'quiz.q.ties.opt.ES' },
      { id: 'FR', label_key: 'quiz.q.ties.opt.FR' },
      { id: 'GB', label_key: 'quiz.q.ties.opt.GB' },
      { id: 'GE', label_key: 'quiz.q.ties.opt.GE' },
      { id: 'GR', label_key: 'quiz.q.ties.opt.GR' },
      { id: 'HR', label_key: 'quiz.q.ties.opt.HR' },
      { id: 'HU', label_key: 'quiz.q.ties.opt.HU' },
      { id: 'ID', label_key: 'quiz.q.ties.opt.ID' },
      { id: 'IE', label_key: 'quiz.q.ties.opt.IE' },
      { id: 'IT', label_key: 'quiz.q.ties.opt.IT' },
      { id: 'ME', label_key: 'quiz.q.ties.opt.ME' },
      { id: 'MT', label_key: 'quiz.q.ties.opt.MT' },
      { id: 'MU', label_key: 'quiz.q.ties.opt.MU' },
      { id: 'MX', label_key: 'quiz.q.ties.opt.MX' },
      { id: 'MY', label_key: 'quiz.q.ties.opt.MY' },
      { id: 'NL', label_key: 'quiz.q.ties.opt.NL' },
      { id: 'PA', label_key: 'quiz.q.ties.opt.PA' },
      { id: 'PL', label_key: 'quiz.q.ties.opt.PL' },
      { id: 'PT', label_key: 'quiz.q.ties.opt.PT' },
      { id: 'PY', label_key: 'quiz.q.ties.opt.PY' },
      { id: 'RS', label_key: 'quiz.q.ties.opt.RS' },
      { id: 'SG', label_key: 'quiz.q.ties.opt.SG' },
      { id: 'TH', label_key: 'quiz.q.ties.opt.TH' },
      { id: 'TR', label_key: 'quiz.q.ties.opt.TR' },
      { id: 'UY', label_key: 'quiz.q.ties.opt.UY' },
      { id: 'VN', label_key: 'quiz.q.ties.opt.VN' },
      { id: 'ZA', label_key: 'quiz.q.ties.opt.ZA' },
    ],
  },
  // Tier-3 #9 (2026-05-25): planning-to-buy signal. Buyers face a second tax
  // queue (annual property tax + purchase tax + capital gains + wealth tax on
  // RE), so this nudges the `cost` and `tax` weights upward to match the
  // reality of the user's cash-flow model. Optional + skippable so the quiz
  // length stays the same for users who don't care. YES also unlocks a
  // property-tax sentence in the result narrative + a chip in PersonaSummary.
  {
    id: 'buy_property',
    type: 'single',
    label_key: 'quiz.q.buy_property.label',
    description_key: 'quiz.q.buy_property.description',
    skippable: true,
    options: [
      // YES → boost cost (purchase tax + ongoing IMU eat budget) and tax
      // (wealth-tax-on-RE + capital-gains affect the bottom line).
      { id: 'yes', label_key: 'quiz.q.buy_property.opt.yes', weights: { cost: 0.3, tax: 0.3 } },
      { id: 'no', label_key: 'quiz.q.buy_property.opt.no' },
      { id: 'unsure', label_key: 'quiz.q.buy_property.opt.unsure' },
    ],
  },
];

export const QUESTIONS_BY_ID: Record<QuestionId, Question> = QUESTIONS.reduce(
  (acc, q) => {
    acc[q.id] = q;
    return acc;
  },
  {} as Record<QuestionId, Question>,
);

// Countries with known elevated seismic risk — used only for soft penalty.
export const SEISMIC_COUNTRIES = new Set(['IT', 'GR', 'TR', 'MX', 'ID', 'CR', 'PA', 'CO']);

export const DIMENSIONS: readonly Dimension[] = [
  'visa',
  'tax',
  'cost',
  'climate',
  'healthcare',
  'safety',
  'language',
  'jobs',
  'family',
  'banking',
] as const;
